import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import DocumentToolbar from './DocumentToolbar';
import COMPANY_INFO from '../../config/companyInfo';
import api from '../../services/api';

const AuthorityToMakeEntry = () => {
  const { id } = useParams();
  const contentRef = useRef(null);

  const [shipment, setShipment] = useState(null);
  const [hawb, setHawb] = useState(null);
  const [shipperInfo, setShipperInfo] = useState(null);
  const [consigneeInfo, setConsigneeInfo] = useState(null);
  const [notifyInfo, setNotifyInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => { fetchData(); }, [id]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const res = await api.get(`/air-import/${id}`);
      const s = res.data;
      setShipment(s);

      // Use first HAWB for party info
      const firstHawb = s.hawbs && s.hawbs.length > 0 ? s.hawbs[0] : null;
      setHawb(firstHawb);

      const fetchTP = async (name) => {
        if (!name) return null;
        try {
          const r = await api.get(`/trade-partners/search?q=${encodeURIComponent(name)}`);
          if (Array.isArray(r.data) && r.data.length > 0) return r.data[0];
        } catch {}
        return null;
      };

      const [ship, cons, notify] = await Promise.all([
        fetchTP(firstHawb?.shipper),
        fetchTP(firstHawb?.consignee),
        fetchTP(firstHawb?.notify || firstHawb?.customer),
      ]);
      setShipperInfo(ship);
      setConsigneeInfo(cons);
      setNotifyInfo(notify);

      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div style={{ padding: '20px', textAlign: 'center' }}>Loading...</div>;
  if (error) return <div style={{ padding: '20px', color: 'red' }}>Error: {error}</div>;
  if (!shipment) return <div style={{ padding: '20px' }}>No shipment data found</div>;

  const formatDate = (d) => {
    if (!d) return '';
    const dt = new Date(d);
    return `${(dt.getMonth()+1).toString().padStart(2,'0')}-${dt.getDate().toString().padStart(2,'0')}-${dt.getFullYear()}`;
  };

  const today = new Date();
  const prepDate = formatDate(today);
  const getTPBlock = (info, name) => {
    if (!info && !name) return 'N/A';
    const lines = [];
    lines.push(info?.name || name || '');
    if (info?.address) lines.push(info.address);
    const cityLine = [info?.city, info?.state].filter(Boolean).join(', ');
    if (cityLine) lines.push(cityLine);
    if (info?.tel) lines.push(`TEL: ${info.tel}`);
    return lines.filter(Boolean).join('\n');
  };

  const h = hawb || {};
  const freightLoc = shipment.freight_location || h.freight_location || '';

  return (
    <div style={{ padding: '20px', backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
      <DocumentToolbar
        title="Authority to Make Entry"
        contentRef={contentRef}
        pdfUrl={`/pdf/shipment/air-import/${id}/atme`}
        onClose={() => window.close()}
        fileName={`ATME-${shipment.file_no || id}.pdf`}
      />

      <div
        ref={contentRef}
        style={{
          backgroundColor: 'white', padding: '35px 40px', maxWidth: '900px',
          margin: '20px auto', boxShadow: '0 0 10px rgba(0,0,0,0.1)',
          fontFamily: 'Arial, sans-serif', fontSize: '11px', lineHeight: '1.4', color: '#000',
        }}
      >
        {/* Company Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px', paddingBottom: '15px', borderBottom: '3px solid #c00' }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              {COMPANY_INFO.logo && <img src={COMPANY_INFO.logo} alt="" style={{ height: '52px', marginRight: '12px' }} />}
              <div>
              <div style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '4px' }}>{COMPANY_INFO.name}</div>
              <div style={{ fontSize: '10px', lineHeight: '1.6' }}>
              <div>{COMPANY_INFO.address}</div>
              <div>{COMPANY_INFO.cityStateZip}</div>
              <div>TEL: {COMPANY_INFO.tel}</div>
              <div>EMAIL: {COMPANY_INFO.email}</div>
            </div>
              </div>
            </div>
          </div>
          <div style={{ textAlign: 'right', paddingTop: '8px' }}>
            <div style={{ fontSize: '20px', fontWeight: 'bold', letterSpacing: '1px' }}>AUTHORITY TO MAKE ENTRY</div>
          </div>
        </div>

        {/* Parties: Shipper / Consignee / Notify */}
        <div style={{ display: 'flex', border: '1px solid #000', marginBottom: '0' }}>
          <div style={{ flex: 1, padding: '8px', borderRight: '1px solid #000', fontSize: '9px' }}>
            <div style={{ fontWeight: 'bold', fontSize: '10px', marginBottom: '4px' }}>SHIPPER :</div>
            <div style={{ whiteSpace: 'pre-line' }}>{getTPBlock(shipperInfo, h.shipper)}</div>
          </div>
          <div style={{ flex: 1, padding: '8px', borderRight: '1px solid #000', fontSize: '9px' }}>
            <div style={{ fontWeight: 'bold', fontSize: '10px', marginBottom: '4px' }}>CONSIGNEE :</div>
            <div style={{ whiteSpace: 'pre-line' }}>{getTPBlock(consigneeInfo, h.consignee)}</div>
          </div>
          <div style={{ flex: 1, padding: '8px', fontSize: '9px' }}>
            <div style={{ fontWeight: 'bold', fontSize: '10px', marginBottom: '4px' }}>NOTIFY PARTY :</div>
            <div style={{ whiteSpace: 'pre-line' }}>{getTPBlock(notifyInfo, h.notify || h.customer)}</div>
          </div>
        </div>

        {/* Merchandise Imported At */}
        <div style={{ border: '1px solid #000', borderTop: 'none', padding: '6px 8px', fontSize: '10px', fontWeight: 'bold', backgroundColor: '#f9f9f9' }}>
          MERCHANDISE IMPORTED AT : {shipment.destination || ''} &nbsp;&nbsp;&nbsp;
          ON : {formatDate(shipment.arrival_date_time)} &nbsp;&nbsp;&nbsp;
          VIA : {shipment.carrier || ''}
        </div>

        {/* Shipment Details Grid */}
        <div style={{ border: '1px solid #000', borderTop: 'none' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '150px 1fr 150px 1fr', fontSize: '10px' }}>
            {/* Left column labels + values, Right column labels + values */}
            <Cell label="FILE NO. :" value={shipment.file_no || ''} />
            <Cell label="DATE:" value={prepDate} />

            <Cell label="MAWB NO. :" value={shipment.mawb_no || ''} bold />
            <Cell label="PREP. BY:" value="" />

            <Cell label="SUB-AWB NO. :" value="" />
            <Cell label="DEP. AIRPORT:" value={shipment.departure || ''} />

            <Cell label="HAWB NO. :" value={h.hawb_no || shipment.mawb_no || ''} bold />
            <Cell label="ENTRY PORT:" value={shipment.destination || ''} />

            <Cell label="MANIFEST NO. :" value="" />
            <Cell label="DEST. AIRPORT:" value={shipment.destination || ''} />

            <Cell label="FLIGHT NO. :" value={shipment.flight_no || ''} bold />
            <Cell label="FINAL DEST. :" value={h.final_destination || shipment.destination || ''} />
          </div>

          {/* ETD / ETA row */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '4px 10px', fontSize: '10px', borderTop: '1px solid #ccc' }}>
            <span style={{ marginRight: '30px' }}><b>ETD:</b> {formatDate(shipment.departure_date_time)}</span>
            <span><b>ETA:</b> {formatDate(shipment.arrival_date_time)}</span>
          </div>

          {/* Freight Location */}
          <div style={{ display: 'flex', borderTop: '1px solid #000', fontSize: '10px' }}>
            <div style={{ flex: 1, padding: '6px 8px' }}>
              <b>FREIGHT LOC. :</b> {freightLoc}
            </div>
            <div style={{ flex: 0.5, padding: '6px 8px', borderLeft: '1px solid #000' }}>
              <b>I.T. NO. :</b> {h.it_no || ''}
            </div>
          </div>
          <div style={{ display: 'flex', borderTop: '1px solid #ccc', fontSize: '10px' }}>
            <div style={{ flex: 1, padding: '4px 8px' }}>
              <b>LAST FREE DAY:</b> {formatDate(h.last_free_day || shipment.storage_start_date)}
            </div>
            <div style={{ flex: 0.5, padding: '4px 8px', borderLeft: '1px solid #000' }}>
              <b>I.T. DATE:</b> {formatDate(h.it_date)}
            </div>
          </div>
        </div>

        {/* Cargo Table */}
        <div style={{ marginTop: '0' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '10px', border: '1px solid #000', borderTop: 'none' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #000', backgroundColor: '#f9f9f9' }}>
                <th style={thStyle}>MARK</th>
                <th style={thStyle}>PACKAGE</th>
                <th style={thStyle}>DESCRIPTION</th>
                <th style={thStyle}>GROS. WEIGHT</th>
                <th style={thStyle}>CHARG. WEIGHT</th>
              </tr>
            </thead>
            <tbody>
              <tr style={{ borderBottom: '1px solid #ccc', minHeight: '40px' }}>
                <td style={tdStyle}>{h.mark || ''}</td>
                <td style={{ ...tdStyle, textAlign: 'center' }}>{shipment.package_count || h.package_count || ''}</td>
                <td style={tdStyle}>{h.description || ''}</td>
                <td style={{ ...tdStyle, textAlign: 'right' }}>
                  {shipment.gross_weight_kg || h.gross_weight_kg || ''} {(shipment.gross_weight_kg || h.gross_weight_kg) ? 'KGS' : ''}
                </td>
                <td style={{ ...tdStyle, textAlign: 'right' }}>
                  {shipment.chargeable_weight_kg || h.chargeable_weight_kg || ''} {(shipment.chargeable_weight_kg || h.chargeable_weight_kg) ? 'KGS' : ''}
                </td>
              </tr>
              {/* Empty rows for space (matching the PDF layout) */}
              <tr><td style={tdStyle} colSpan="5">&nbsp;</td></tr>
              <tr><td style={tdStyle} colSpan="5">&nbsp;</td></tr>
              {/* Volume Weight / Measurement row */}
              <tr style={{ borderTop: '1px solid #ccc' }}>
                <td colSpan="3" style={tdStyle}></td>
                <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 'bold' }}>VOLUME WEIGHT</td>
                <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 'bold' }}>MEASUREMENT</td>
              </tr>
              <tr>
                <td colSpan="3" style={tdStyle}></td>
                <td style={{ ...tdStyle, textAlign: 'right' }}>
                  {shipment.volume_weight_kg || h.volume_weight_kg || ''} {(shipment.volume_weight_kg || h.volume_weight_kg) ? 'KGS' : ''}
                </td>
                <td style={{ ...tdStyle, textAlign: 'right' }}>
                  {shipment.measurement_cbm || h.measurement_cbm || ''} {(shipment.measurement_cbm || h.measurement_cbm) ? 'CBM' : ''}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Remark */}
        <div style={{ border: '1px solid #000', borderTop: 'none', padding: '8px', minHeight: '40px', fontSize: '10px' }}>
          <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>REMARK</div>
          <div style={{ whiteSpace: 'pre-wrap' }}>{h.remark || shipment.memo || ''}</div>
        </div>

        {/* Authorization Statement */}
        <div style={{ border: '1px solid #000', borderTop: 'none', padding: '15px 10px', fontSize: '10px', lineHeight: '2' }}>
          <div>
            <i>WE, </i><b>{COMPANY_INFO.name}</b><i> ,</i>
          </div>
          <div><i>THE CONSIGNEE FOR ABOVE MENTIONED BILL OF LADING COVERING</i></div>
          <div><i>MERCHANDISE FOR VARIOUS ULTIMATE CONSIGNEE, HEREBY AUTHORIZES</i></div>
          <div style={{ borderBottom: '1px solid #000', height: '20px', marginBottom: '4px' }}></div>
          <div style={{ textAlign: 'center' }}><i>TO MAKE CUSTOMS</i></div>
          <div>
            <b>ENTRY AND</b>&nbsp;&nbsp;
            <span style={{ borderBottom: '1px solid #000', display: 'inline-block', minWidth: '200px', paddingBottom: '2px' }}></span>
            &nbsp;&nbsp;<b>TO TAKE DELIVERY</b>
          </div>
          <div><i>FOR THE ABOVE DESCRIBED MERCHANDISE.</i></div>
          <div style={{ textAlign: 'center', marginTop: '15px' }}>
            <div style={{ fontWeight: 'bold', fontSize: '11px' }}>{COMPANY_INFO.name}</div>
            <div style={{ fontSize: '10px' }}>Attorney - In - Fact</div>
          </div>
        </div>

        {/* Document Picked By */}
        <div style={{ border: '1px solid #000', borderTop: 'none', padding: '10px', display: 'flex', fontSize: '10px', fontWeight: 'bold' }}>
          <div style={{ flex: 1 }}>DOCUMENT PICKED BY :</div>
          <div style={{ flex: 0.5 }}>DATE :</div>
          <div style={{ flex: 0.5 }}>TIME :</div>
        </div>
      </div>
    </div>
  );
};

const Cell = ({ label, value, bold }) => (
  <>
    <div style={{ padding: '4px 8px', fontWeight: 'bold', fontSize: '10px', borderBottom: '1px solid #eee' }}>{label}</div>
    <div style={{ padding: '4px 8px', fontSize: bold ? '11px' : '10px', fontWeight: bold ? 'bold' : 'normal', borderBottom: '1px solid #eee' }}>{value}</div>
  </>
);

const thStyle = {
  padding: '6px 8px', textAlign: 'left', fontWeight: 'bold',
  borderRight: '1px solid #000', borderBottom: '1px solid #000',
};

const tdStyle = {
  padding: '6px 8px', borderRight: '1px solid #000',
};

export default AuthorityToMakeEntry;
